/***************************************************
 * Created by gongyanyu on 2022/10/18 14:12. *
 ***************************************************/
import { $Keys, Overwrite, PickByValue } from 'utility-types';
import { intercept, IValueDidChange, observe } from 'mobx';

import { createDataRef, DataReferCreator } from '../helpers/initDataRef';
import { initExtends } from '../helpers/initExtends';
import { Meta } from './Meta';
import { FALLBACK_AT_PROPS } from '../helpers/consts';
import type { IInterceptor, IValueWillChange } from 'mobx/src/internal';
import type { IDisposer } from './types';
import { makeObservable, observable } from 'mobx';
import { unbox } from '../helpers/unbox';

export class ViewModel {
  constructor(...args: any[]) {
    makeObservable(this);
    this.$meta.definition = args[0];
    this.$meta.type = this.$meta.definition?.vmType;
    this.$name = this.$meta.definition?.name;
  }

  readonly $meta: Meta = new Meta();

  readonly $name: string;

  $ref(vm: ViewModel, key: string, defaultValue?: any) {
    return createDataRef(vm, key, defaultValue);
  }

  @observable.ref
  $parent: ViewModel | null = null;

  @observable.shallow
  $children: Set<ViewModel> = new Set<ViewModel>();

  $upper(type: string): ViewModel | null {
    let current = this.$parent;
    while (current) {
      if (current.$meta.type === type) {
        return current;
      }
      current = current.$parent;
    }
    return null;
  }

  $pickit<T>(props: T): T {
    const oo: Record<string, any> = {};
    this.$meta.properties.forEach((key) => {
      oo[key] =
        this[key] === FALLBACK_AT_PROPS //
          ? props[key as keyof T]
          : unbox(this, key, this.$meta.auto_unbox);
    });
    this.$meta.handlers.forEach((key) => {
      oo[key] = (...args: any[]) => {
        this[key]?.(...args);
        (props as any)[key]?.(...args);
      };
      return oo;
    });
    return { ...props, ...oo } as T;
  }

  /**
   * 属性/方法注册函数, 将会把`properties`中的定义全部合并到当前实例中
   *   注意:
   *    如果当前实例属性已经存在, 将会调用`更新`方法
   *    如果当前实例属性不存在, 将会调用`注册`方法
   * @param properties
   */
  $register<T extends object>(properties: T): Overwrite<T, Record<$Keys<PickByValue<T, DataReferCreator>>, any>> & this {
    initExtends(this, properties);
    return this as any;
  }

  $observe<T>(key: string, listener: (change: IValueDidChange<T>) => void, invokeImmediately?: boolean): IDisposer;
  $observe<T>(listener: (change: IValueDidChange<T>) => void, invokeImmediately?: boolean): IDisposer;
  $observe<T>(key: any, listener: any, invokeImmediately?: boolean): IDisposer {
    return observe(this, key, listener, invokeImmediately);
  }

  $intercept<T>(key: string, handler: IInterceptor<IValueWillChange<T>>): IDisposer;
  $intercept<T>(handler: IInterceptor<IValueWillChange<T>>): IDisposer;
  $intercept<T>(key: any, handler?: any): IDisposer {
    return intercept(this, key, handler);
  }

  [key: string]: any;
}
